import _ from 'underscore';
import { CampaignType } from '@bingads-webui-campaign/campaign-ui-common';
import { projectionGridBulkEdit } from '@bingads-webui-campaign/projection-grid-bulk-edit';
import { campaignAudienceAssociationDecoration, onSuccess as onAudienceAssociationSuccess } from './audience-decoration';

/**
 * @typedef { Object } campaignBulkEditOptions
 * @property { Object } status campaign status schema.
 * @property { Object } ChangeStatusView campaign change status view.
 * @property { Object } ChangeStatusModel campaign change status model.
 * @property { Object } ChangeBudgetView campaign change budget view.
 * @property { Object } ChangeBudgetModel campaign change budget model.
 * @property { Object } ChangeCashbackBudgetView campaign change cash budget view.
 * @property { Object } ChangeCashbackBudgetModel campaign change cash budget model.
 * @property { Object } ChangeCashbackPercentView campaign change cash percent view.
 * @property { Object } ChangeCashbackPercentModel campaign change cash percent model.
 * @property { Object } ChangeBidStrategyModel campaign change bid strategy model.
 * @property { Object } ChangeBidStrategyView campaign change bid strategy model.
 * @property { Object } BulkCampaignExclusionsView campaign bulk add exclusion view.
 * @property { Object } ChangeLanguageView campaign change language view.
 * @property { Object } ChangeLanguageModel campaign change language model.
 * @property { Object } AudienceAssociationView campaign association exclusion view.
 * @property { Object } AudienceAssociationModel campaign association exclusion model.
 * @property { Object } ChangeRsaAutoAssetsView campaign change RSA auto assets view.
 * @property { Object } ChangeRsaAutoAssetsModel campaign change RSA auto assets model.
 * @property { Object } CopyPaste campaign copy paste
 * @property { Object } config config.
 * @property { Object } i18n i18n.
 * @property { Object } scope scope.
 * @property { Object } isForVNext isForVNext.
 * @property { String } bulkEditUrl BulkEditUrl.
 * @property { Object } disableDeselectAll flag to disable deselect all after bulk edit success.
 */

/**
 * campaignBulkEdit
 * @param { campaignBulkEditOptions } options campaign bulk edit options
 * @returns { Object } campaignBulkEditPlugin campaign bulk edit plugin
 */
function campaignBulkEdit(options) {
  const {
    status,
    ChangeStatusView,
    ChangeStatusModel,
    ChangeBudgetView,
    ChangeBudgetModel,
    ChangeCashbackBudgetView,
    ChangeCashbackBudgetModel,
    ChangeCashbackPercentView,
    ChangeCashbackPercentModel,
    ChangeBidStrategyModel,
    ChangeBidStrategyView,
    ChangeLanguageView,
    ChangeLanguageModel,
    BulkCampaignExclusionsView,
    AudienceAssociationView,
    AudienceAssociationModel,
    ChangeRsaAutoAssetsView,
    ChangeRsaAutoAssetsModel,
    CopyPaste,
    config,
    filterService,
    i18n,
    i18nNewDesign,
    router,
    scope,
    isForVNext,
    bulkEditUrl,
    qualtricsSurveyCallback,
    importNotificationCallback,
    importBudgetBidNotificationCallback,
    disableDeselectAll = false,
    getCampaignTypeGlobalFilter,
    perfMarker,
    campaignUpgradeProps,
    cacheManager,
  } = options;

  function isFilterByPerformanceMaxCampaigns() {
    return getCampaignTypeGlobalFilter() === CampaignType.PerformanceMax;
  }

  function isFilterByAppAdsCampaigns() {
    return getCampaignTypeGlobalFilter() === CampaignType.App;
  }

  function isFilterByAllCampaigns() {
    return getCampaignTypeGlobalFilter() === CampaignType.All;
  }

  function isFilterBySearchCampaigns() {
    return getCampaignTypeGlobalFilter() === CampaignType.Default;
  }

  function isFilterByShoppingCampaigns() {
    return getCampaignTypeGlobalFilter() === CampaignType.Shopping;
  }

  function getChangeBudgetView({ viewConfig, selectedRowCount }) {
    return new ChangeBudgetView(_.defaults({
      model: new ChangeBudgetModel({
        selectedEntityCount: selectedRowCount,
        scope,
        budgetType: 'Daily',
      }),
      isForVNext,
    }, viewConfig));
  }

  function getChangeLifetimeBudgetView({ viewConfig, selectedRowCount }) {
    return new ChangeBudgetView(_.defaults({
      model: new ChangeBudgetModel({
        selectedEntityCount: selectedRowCount,
        scope,
        budgetType: 'LifetimeBudgetStandard',
      }),
      isForVNext,
    }, viewConfig));
  }

  function getChangeCashbackPercentView({ viewConfig, selectedRowCount }) {
    return new ChangeCashbackPercentView(_.defaults({
      model: new ChangeCashbackPercentModel({
        selectedEntityCount: selectedRowCount,
        scope,
      }),
      isForVNext,
    }, viewConfig));
  }

  function getChangeCashbackBudgetView({ viewConfig, selectedRowCount }) {
    return new ChangeCashbackBudgetView(_.defaults({
      model: new ChangeCashbackBudgetModel({
        selectedEntityCount: selectedRowCount,
        scope,
      }),
      isForVNext,
    }, viewConfig));
  }

  function getChangeBidStrategyView({ viewConfig, selectedRowCount }) {
    return new ChangeBidStrategyView(_.defaults({
      model: new ChangeBidStrategyModel({
        Entity: 'Campaign',
        selectedEntityCount: selectedRowCount,
        scope,
      }),
      isForVNext,
      entityType: 'Campaign',
      biddingStrategyType: 'EnhancedCpc',
      i18nNewDesign,
      getCampaignTypeGlobalFilter,
    }, viewConfig));
  }

  function getChangeLanguageView({ viewConfig, selectedRowCount }) {
    return new ChangeLanguageView(_.defaults({
      model: new ChangeLanguageModel({
        selectedEntityCount: selectedRowCount,
        scope,
      }),
      isForVNext,
    }, viewConfig));
  }

  function getaddExclusionsView({ viewConfig }) {
    if (viewConfig.grid.pgridPlugins.crossPageSelectAll.isAllSelected && !confirm(i18n.getString('CampaignSummaryPage_MultiplePageSelectionNotAllowed'))) {// eslint-disable-line
      return undefined;
    }

    const { gridView } = viewConfig.grid;
    const { inlineError } = viewConfig;

    return new BulkCampaignExclusionsView({
      gridView,
      inlineError,
      isForVNext,
      grid: viewConfig.grid,
      campaignType: getCampaignTypeGlobalFilter(),
      cacheManager,
    });
  }

  function getCampaignAudienceView({ viewConfig, selectedRowCount }) {
    const { IsCustomAudienceAllowed, IsInMarketAudienceAllowed } = config.get('Permissions');
    let audienceAssociationPerfMarker;
    if (perfMarker) {
      audienceAssociationPerfMarker = perfMarker.createChild('Association_With_Audiences');
      audienceAssociationPerfMarker.willInit();
      const { gridView } = viewConfig.grid;
      const listener = () => {
        audienceAssociationPerfMarker.done();
        gridView.off('didUpdate', listener);
      };
      gridView.on('didUpdate', listener);
    }
    return new AudienceAssociationView(_.defaults(
      {
        isForVNext,
        model: new AudienceAssociationModel({ selectedEntityCount: selectedRowCount }),
        router,
        perfMarker: audienceAssociationPerfMarker,
        newI18n: i18nNewDesign,
      },
      viewConfig,
      isForVNext ? {
        onSuccessCallback: () => onAudienceAssociationSuccess({
          i18n,
          isCustomInMarketEnabled: IsCustomAudienceAllowed || IsInMarketAudienceAllowed,
          audiencesPath: `${router.getRoutePathById('audiences')}${window.location.search}`,
        }),
      } : {}
    ));
  }

  function getChangeRsaAutoAssetsView({ viewConfig, selectedRowCount }) {
    return new ChangeRsaAutoAssetsView(_.defaults({
      model: new ChangeRsaAutoAssetsModel({
        selectedEntityCount: selectedRowCount,
        scope,
      }),
    }, viewConfig));
  }

  function onCopyClicked({ viewConfig }) {
    CopyPaste.copy('campaign', Object.assign({}, viewConfig.grid, { perfMarker }));
  }

  function onPasteClicked({ viewConfig }) {
    CopyPaste.paste(Object.assign({}, viewConfig.grid, { perfMarker }));
  }

  const permissions = config.get('Permissions');

  const { CampaignLifetimeBudget, MaeLifetimeBudgetExclusion } = permissions.dynamic || {};
  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  const items = [{
    text: isCampaignLifetimeBudgetEnabled ? i18n.getString('Grid_ChangeDailyBudgets') : i18n.getString('Grid_ChangeBudgets'),
    classes: ['bulk-edit-change-budget'],
    createView: getChangeBudgetView,
  },
  isCampaignLifetimeBudgetEnabled && {
    text: i18n.getString('Grid_ChangeLifetimeBudgets'),
    classes: ['bulk-edit-change-lifetime-budget'],
    createView: getChangeLifetimeBudgetView,
  },
  permissions.IsPersonalizedOffersEnabled &&
  !permissions.IsPersonalizedOffersV4Enabled &&
  !(permissions.IsPersonalizedOffersV2Enabled ||
  permissions.IsPersonalizedOffersV3Enabled) &&
  isForVNext ? {
      text: i18n.getString('Grid_ChangeCashbackBudgets'),
      classes: ['bulk-edit-change-cashback-budget'],
      createView: getChangeCashbackBudgetView,
    } : null,
  permissions.IsPersonalizedOffersEnabled &&
  !permissions.IsPersonalizedOffersV4Enabled &&
  isForVNext ? {
      text: i18n.getString('Grid_ChangeCashbackPercent'),
      classes: ['bulk-edit-change-cashback-percent'],
      createView: getChangeCashbackPercentView,
    } : null,
  isForVNext ? {
    text: i18n.getString('Grid_BidStrategy'),
    classes: ['bulk-edit-change-bid-strategy-type'],
    createView: getChangeBidStrategyView,
    isValid: () => !isFilterByAppAdsCampaigns(),
  } : null,
  {
    text: i18n.getString('EditCampaignLanguage'),
    classes: ['bulk-edit-campaign-language'],
    createView: getChangeLanguageView,
  },
  {
    text: i18n.getString('Grid_BulkChanges'),
    classes: ['bulk-edit-exclusions'],
    createView: getaddExclusionsView,
  },
  ];

  const decorationCallback = (gridView) => {
    const localData = {
      i18n,
      isCustomInMarketEnabled:
        permissions.IsCustomAudienceAllowed || permissions.IsInMarketAudienceAllowed,
    };

    campaignAudienceAssociationDecoration(gridView, localData);
  };

  items.push({
    text: permissions.IsCustomAudienceAllowed || permissions.IsInMarketAudienceAllowed ?
      i18n.getString('AudiencesAssociation_BulkEditAssociation_Audience_Title') : i18n.getString('AudiencesAssociation_BulkEditAssociation_Title'),
    classes: ['bulk-edit-campaign-audience'],
    createView: getCampaignAudienceView,
    isFallbackEnabled: false,
    isValid: () => !isFilterByPerformanceMaxCampaigns() && !isFilterByAppAdsCampaigns(),
  });

  const showUpgradeTool = scope && scope.location
    && scope.location.pathname === '/campaign/vnext/campaigns';

  const campaignUpgradeItem = showUpgradeTool ? {
    text: _.result(campaignUpgradeProps, 'toolbarDropdownText'),
    classes: ['pmax-campaign-upgrade'],
    repos: campaignUpgradeProps,
    isValid: () => isFilterByAllCampaigns() || isFilterByShoppingCampaigns(),
  } : {
    isValid: false,
  };

  let frontItems = [];
  if (permissions.IsCampaignAdGroupCopyPasteEnabled && CopyPaste) {
    if (isForVNext) {
      frontItems = [{
        text: i18n.getString('BulkEdit_CopyMenu'),
        classes: ['bulk-edit-campaigns-copy'],
        createView: onCopyClicked,
        hotkeys: {
          win: 'Ctrl-C',
          mac: '\u2318C',
        },
        isValid: () => {
          if (isFilterByPerformanceMaxCampaigns()) {
            return _.get(permissions, ['dynamic', 'PmaxCopyPasteEnabled'], false);
          }
          if (isFilterByAppAdsCampaigns()) {
            return false;
          }
          return true;
        },
      }];
    } else {
      items.push(
        {
          text: i18n.getString('BulkEdit_CopyMenu'),
          classes: ['bulk-edit-campaigns-copy'],
          createView: onCopyClicked,
        },
        {
          text: i18n.getString('BulkEdit_PasteMenu'),
          classes: ['bulk-edit-campaigns-paste'],
          createView: onPasteClicked,
          noSelectionRequired: true,
        }
      );
    }
  }

  // Since the campaignBulkEdit is used in multi grids,
  // add extra View & Model check to avoid impacting other unneeded grids.
  if (ChangeRsaAutoAssetsView && ChangeRsaAutoAssetsModel) {
    items.push({
      text: i18n.getString('Grid_ChangeRsaAutoAssets'),
      classes: ['bulk-edit-rsa-auto-assets'],
      createView: getChangeRsaAutoAssetsView,
      isValid: () => isFilterByAllCampaigns() || isFilterBySearchCampaigns(),
    });
  }

  return projectionGridBulkEdit({
    status,
    ChangeStatusView,
    ChangeStatusModel,
    DeleteConfirmation: i18n.getString('Campaigns_DeleteConfirmation'),
    config,
    i18n,
    entityType: 'campaign',
    items: _.compact(items),
    frontItems,
    campaignUpgradeItem,
    filterService,
    scope,
    gridDecorationCallback: decorationCallback,
    isForVNext,
    bulkEditUrl,
    qualtricsSurveyCallback,
    importNotificationCallback,
    importBudgetBidNotificationCallback,
    disableDeselectAll,
  });
}

export { campaignBulkEdit };
